import React, { useEffect } from "react";
import { trackEvent } from "../../analytics";

const faqItems = [
  {
    question: "What is CSRD reporting?",
    answer:
      "CSRD reporting is the sustainability reporting process required under the Corporate Sustainability Reporting Directive. It asks companies to disclose material environmental, social, and governance information using European Sustainability Reporting Standards."
  },
  {
    question: "Who needs to prepare for CSRD?",
    answer:
      "Large EU companies, listed SMEs, and many non-EU companies with significant EU activity may need to prepare CSRD-aligned disclosures. Suppliers and SMEs can also face indirect pressure from enterprise customers and investors."
  },
  {
    question: "How can AI help with CSRD reporting?",
    answer:
      "AI can help teams structure ESG inputs, identify disclosure gaps, draft reviewable narratives, summarize evidence, and prepare management-ready reporting packs. Human review remains essential for final disclosure quality."
  },
  {
    question: "What is the first step for CSRD readiness?",
    answer:
      "The first step is to map reporting obligations, identify material sustainability topics, assess data availability, and create a gap remediation plan before drafting disclosures."
  }
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqItems.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer
    }
  }))
};

export default function CSRDReportingGuide() {
  useEffect(() => {
    trackEvent("guide_view", {
      guide: "csrd_reporting",
      path: window.location.pathname
    });
  }, []);

  const bookDemo = () => {
    trackEvent("demo_click", {
      source: "csrd_reporting_guide",
      path: window.location.pathname
    });

    window.location.href =
      "mailto:hello@onedigitale.com?subject=Ecovanta CSRD Reporting Demo Request";
  };

  return (
    <main style={pageStyle}>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <section style={heroStyle}>
        <a href="/" style={backLinkStyle}>← Back to Ecovanta</a>
        <div style={eyebrowStyle}>CSRD Reporting Guide</div>
        <h1 style={titleStyle}>CSRD Reporting: A Practical Guide for Companies Preparing ESG Disclosures</h1>
        <p style={introStyle}>
          CSRD reporting is becoming a major driver of sustainability governance, investor communication, and supplier transparency. This guide explains what CSRD means, what companies need to prepare, and how AI-assisted ESG reporting can reduce manual work while improving disclosure quality.
        </p>
        <div style={ctaRowStyle}>
          <button style={primaryButtonStyle} onClick={bookDemo}>Book a CSRD Demo</button>
          <a href="/faq" style={secondaryButtonStyle}>Read FAQ</a>
        </div>
      </section>

      <section style={contentStyle}>
        <div style={tocStyle}>
          <strong>In this guide</strong>
          <a href="#what-is-csrd">What is CSRD?</a>
          <a href="#who-is-affected">Who is affected?</a>
          <a href="#requirements">Core requirements</a>
          <a href="#ai">How AI helps</a>
          <a href="#checklist">Readiness checklist</a>
          <a href="#faq">FAQ</a>
        </div>

        <article style={articleStyle}>
          <section id="what-is-csrd" style={sectionStyle}>
            <h2>What is CSRD reporting?</h2>
            <p>
              CSRD stands for Corporate Sustainability Reporting Directive. It expands sustainability reporting expectations in Europe and requires many companies to provide structured, comparable, and decision-useful information about environmental, social, and governance impacts, risks, and opportunities.
            </p>
            <p>
              For business leaders, CSRD is more than a compliance exercise. It creates a repeatable operating model for sustainability data, governance, performance management, and stakeholder communication.
            </p>
          </section>

          <section id="who-is-affected" style={sectionStyle}>
            <h2>Who is affected by CSRD?</h2>
            <p>
              CSRD directly affects many large companies and listed SMEs. It can also affect suppliers, subsidiaries, and non-EU organizations that serve enterprise customers or operate in European markets. Even companies not directly in scope may need better ESG data because customers, lenders, and investors request CSRD-aligned information.
            </p>
          </section>

          <section id="requirements" style={sectionStyle}>
            <h2>Core CSRD reporting requirements</h2>
            <div style={gridStyle}>
              <div style={cardStyle}><h3>Double materiality</h3><p>Companies assess both sustainability impacts and financial effects of sustainability matters.</p></div>
              <div style={cardStyle}><h3>ESRS alignment</h3><p>Disclosures are structured around European Sustainability Reporting Standards.</p></div>
              <div style={cardStyle}><h3>Evidence quality</h3><p>Teams need reliable data, clear ownership, and reviewable assumptions.</p></div>
              <div style={cardStyle}><h3>Governance</h3><p>Leadership must understand material issues, risks, targets, and progress.</p></div>
            </div>
          </section>

          <section id="ai" style={sectionStyle}>
            <h2>How AI can help with CSRD reporting</h2>
            <p>
              AI can accelerate CSRD preparation when it is used as a controlled drafting and analysis layer rather than a black-box compliance tool. The strongest use cases include gap analysis, evidence summarization, draft disclosure narratives, KPI recommendations, and management reporting.
            </p>
            <p>
              Ecovanta is designed to help teams move from fragmented spreadsheets and documents toward structured ESG workflows, AI-assisted disclosure drafting, and investor-grade reporting outputs.
            </p>
          </section>

          <section id="checklist" style={sectionStyle}>
            <h2>CSRD readiness checklist</h2>
            <ul style={listStyle}>
              <li>Confirm whether the company is directly or indirectly affected by CSRD.</li>
              <li>Map ESG data owners across finance, sustainability, HR, operations, and governance.</li>
              <li>Perform a double materiality assessment.</li>
              <li>Identify disclosure gaps against ESRS topics.</li>
              <li>Create a remediation plan with owners, timelines, and KPIs.</li>
              <li>Draft disclosures and validate them with internal stakeholders.</li>
              <li>Prepare board-ready and investor-ready reporting packs.</li>
            </ul>
          </section>

          <section id="faq" style={sectionStyle}>
            <h2>CSRD reporting FAQ</h2>
            {faqItems.map((item) => (
              <div key={item.question} style={faqStyle}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </div>
            ))}
          </section>

          <section style={ctaBoxStyle}>
            <h2>Prepare CSRD reporting with Ecovanta</h2>
            <p>
              Ecovanta helps organizations collect ESG inputs, identify reporting gaps, generate reviewable AI-assisted disclosures, and create decision-ready sustainability reporting packs.
            </p>
            <button style={primaryButtonStyle} onClick={bookDemo}>Book a CSRD Demo</button>
          </section>
        </article>
      </section>
    </main>
  );
}

const pageStyle = { fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', background: "#f8fafc", color: "#0f172a", minHeight: "100vh" };
const heroStyle = { maxWidth: "1120px", margin: "0 auto", padding: "56px 24px 32px" };
const backLinkStyle = { color: "#475569", textDecoration: "none", fontWeight: 700 };
const eyebrowStyle = { marginTop: "32px", color: "#047857", fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "13px" };
const titleStyle = { fontSize: "clamp(36px, 5vw, 64px)", lineHeight: 1.02, margin: "16px 0", letterSpacing: "-0.04em" };
const introStyle = { fontSize: "20px", lineHeight: 1.7, color: "#475569", maxWidth: "880px" };
const ctaRowStyle = { display: "flex", gap: "14px", flexWrap: "wrap", marginTop: "28px" };
const primaryButtonStyle = { border: "none", background: "#0f172a", color: "#fff", borderRadius: "14px", padding: "14px 20px", fontWeight: 900, cursor: "pointer" };
const secondaryButtonStyle = { border: "1px solid #cbd5e1", background: "#fff", color: "#0f172a", borderRadius: "14px", padding: "14px 20px", fontWeight: 900, textDecoration: "none" };
const contentStyle = { maxWidth: "1120px", margin: "0 auto", padding: "24px", display: "grid", gridTemplateColumns: "260px minmax(0, 1fr)", gap: "32px" };
const tocStyle = { alignSelf: "start", position: "sticky", top: "88px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "18px", padding: "18px", display: "grid", gap: "12px", boxShadow: "0 10px 30px rgba(15,23,42,0.06)" };
const articleStyle = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "24px", padding: "32px", boxShadow: "0 16px 40px rgba(15,23,42,0.06)" };
const sectionStyle = { marginBottom: "42px", lineHeight: 1.75, color: "#334155", fontSize: "17px" };
const gridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "16px" };
const cardStyle = { border: "1px solid #e2e8f0", borderRadius: "18px", padding: "18px", background: "#f8fafc" };
const listStyle = { paddingLeft: "22px" };
const faqStyle = { borderTop: "1px solid #e2e8f0", paddingTop: "18px", marginTop: "18px" };
const ctaBoxStyle = { background: "#0f172a", color: "#fff", borderRadius: "24px", padding: "28px", lineHeight: 1.7 };